/**
 * Integration Enhanced Component
 * Unique Properties: API configuration, webhooks, authentication, data mapping, testing
 */

import { Alert, Badge, Button, Card, Code, Container, Grid, Group, JsonInput, Paper, Select, Stack, Tabs, Text, TextInput, Title } from '@mantine/core';
import '@mantine/core/styles.css';
import { IconApi, IconKey, IconTestPipe, IconWebhook } from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import WorkflowSearchMenu from './WorkflowSearchMenu';

interface IntegrationProps {
    data?: any;
    mode?: 'configure' | 'test' | 'monitor';
}

export const IntegrationEnhanced: React.FC<IntegrationProps> = ({ data = {}, mode = 'configure' }) => {
    const [activeTab, setActiveTab] = useState('endpoints');
    const [testResult, setTestResult] = useState<any>(null);
    const tableRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (tableRef.current) {
            new TabulatorFull(tableRef.current, {
                data: [],
                layout: 'fitColumns',
                columns: [
                    { title: 'Endpoint', field: 'endpoint', editor: 'input' },
                    { title: 'Method', field: 'method', editor: 'list', editorParams: { values: ['GET', 'POST', 'PUT', 'DELETE'] } },
                    { title: 'Auth Type', field: 'auth' },
                    { title: 'Status', field: 'status', formatter: (cell: any) => {
                        const value = cell.getValue();
                        const color = value === 'active' ? 'green' : value === 'error' ? 'red' : 'orange';
                        return `<span style="color: ${color};">${value}</span>`;
                    }}
                ]
            });
        }
    }, []);

    return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Title order={2} mb="lg">Integration Configuration</Title>
                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="endpoints" leftSection={<IconApi size={16} />}>Endpoints</Tabs.Tab>
                        <Tabs.Tab value="webhooks" leftSection={<IconWebhook size={16} />}>Webhooks</Tabs.Tab>
                        <Tabs.Tab value="auth" leftSection={<IconKey size={16} />}>Authentication</Tabs.Tab>
                        <Tabs.Tab value="testing" leftSection={<IconTestPipe size={16} />}>Testing</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="endpoints">
                        <Stack mt="md">
                            <div ref={tableRef} />
                            <Grid>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Title order={5} mb="md">Add New Endpoint</Title>
                                        <Stack gap="xs">
                                            <TextInput label="URL" placeholder="https://api.example.com/v1" />
                                            <Select
                                                label="Method"
                                                data={['GET', 'POST', 'PUT', 'DELETE', 'PATCH']}
                                                defaultValue="GET"
                                            />
                                            <TextInput label="Headers" placeholder="Content-Type: application/json" />
                                            <Button>Add Endpoint</Button>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Title order={5} mb="md">Active Integrations</Title>
                                        <Stack gap="xs">
                                            <Group justify="space-between">
                                                <Text size="sm">Stripe API</Text>
                                                <Badge color="green">Active</Badge>
                                            </Group>
                                            <Group justify="space-between">
                                                <Text size="sm">SendGrid</Text>
                                                <Badge color="green">Active</Badge>
                                            </Group>
                                            <Group justify="space-between">
                                                <Text size="sm">Twilio</Text>
                                                <Badge color="orange">Configured</Badge>
                                            </Group>
                                            <Group justify="space-between">
                                                <Text size="sm">Slack</Text>
                                                <Badge color="red">Error</Badge>
                                            </Group>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Tabs.Panel>
                    <Tabs.Panel value="webhooks">
                        <Stack mt="md">
                            <Alert color="blue">
                                Configure webhook endpoints to receive real-time events
                            </Alert>
                            <Card withBorder>
                                <Title order={5} mb="md">Webhook Configuration</Title>
                                <Stack gap="xs">
                                    <TextInput label="Webhook URL" placeholder="https://your-app.com/webhook" />
                                    <Select
                                        label="Events"
                                        multiple
                                        data={['workflow.started', 'workflow.completed', 'workflow.failed', 'task.assigned']}
                                    />
                                    <TextInput label="Secret Key" type="password" placeholder="webhook-secret-key" />
                                    <Button>Save Webhook</Button>
                                </Stack>
                            </Card>
                        </Stack>
                    </Tabs.Panel>
                    <Tabs.Panel value="auth">
                        <Stack mt="md">
                            <Grid>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Title order={5} mb="md">OAuth 2.0</Title>
                                        <Stack gap="xs">
                                            <TextInput label="Client ID" />
                                            <TextInput label="Client Secret" type="password" />
                                            <TextInput label="Authorization URL" />
                                            <TextInput label="Token URL" />
                                            <Button>Configure OAuth</Button>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Title order={5} mb="md">API Key</Title>
                                        <Stack gap="xs">
                                            <TextInput label="API Key" type="password" />
                                            <Select
                                                label="Header Name"
                                                data={['X-API-Key', 'Authorization', 'Custom']}
                                                defaultValue="X-API-Key"
                                            />
                                            <Button>Save API Key</Button>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Tabs.Panel>
                    <Tabs.Panel value="testing">
                        <Stack mt="md">
                            <Card withBorder>
                                <Title order={5} mb="md">Test Integration</Title>
                                <Stack gap="xs">
                                    <Select
                                        label="Select Endpoint"
                                        data={['GET /api/users', 'POST /api/orders', 'PUT /api/products']}
                                    />
                                    <JsonInput
                                        label="Request Body"
                                        placeholder="Enter JSON body"
                                        minRows={5}
                                    />
                                    <Button onClick={() => setTestResult({ status: 200, body: { success: true } })}>
                                        Run Test
                                    </Button>
                                </Stack>
                            </Card>
                            {testResult && (
                                <Card withBorder>
                                    <Title order={5} mb="md">Test Result</Title>
                                    <Badge color={testResult.status === 200 ? 'green' : 'red'}>
                                        Status: {testResult.status}
                                    </Badge>
                                    <Code block mt="xs">
                                        {JSON.stringify(testResult.body, null, 2)}
                                    </Code>
                                </Card>
                            )}
                        </Stack>
                    </Tabs.Panel>
                </Tabs>
            </Paper>
        </Container>
    );
};

export default IntegrationEnhanced;
